import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { LockBadge, useToast, useConfirm } from '@/components/ui';
import { connectorsApi, ConnectorInstall } from '@/lib/connectors';
import { crmImportApi, CrmImportRun } from '@/lib/crmImportApi';
import { inboxSyncApi } from '@/lib/inboxSyncApi';
import { usePlan } from '@/hooks/usePlan';

/** Connectors page: per-install status + manual sync + recent runs. */
export function ConnectorsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const plan = usePlan();
  // CRM + inbox connectors require Growth. We still let users *see* their
  // installs at the free tier so they know what's available; we just disable
  // the Run/Sync buttons and surface a LockBadge per row.
  const connectorsLocked = !plan.isLoading && !plan.meets('growth');

  const installsQ = useQuery({
    queryKey: ['connectors-installs', wsId],
    queryFn: () => connectorsApi.listInstalls(wsId),
    enabled: !!wsId,
  });

  const installs = installsQ.data ?? [];
  const isEmpty = !installsQ.isLoading && installs.length === 0;

  return (
    <AppShell titleKey="crm_import.title">
      <div className="mb-6">
        <p className="text-sm text-ink-600 max-w-2xl">{t('crm_import.intro')}</p>
      </div>

      {installsQ.isLoading && (
        <p className="text-sm text-ink-500">{t('common.loading')}</p>
      )}

      {isEmpty && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('crm_import.empty_title')}</p>
          <p className="text-xs mt-2">{t('crm_import.empty_sub')}</p>
        </div>
      )}

      {!isEmpty && !installsQ.isLoading && (
        <ul className="space-y-3">
          {installs.map((inst) => (
            <ConnectorRow
              key={inst.id}
              install={inst}
              workspaceId={wsId}
              planLocked={connectorsLocked}
            />
          ))}
        </ul>
      )}
    </AppShell>
  );
}

// ──────────────────────────────────────────────────────────────

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

function statusBadge(status: CrmImportRun['status'], t: (k: string) => string): JSX.Element {
  const color =
    status === 'succeeded'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'failed'
        ? 'bg-warn-50 text-warn-700'
        : status === 'running'
          ? 'bg-accent-50 text-accent-700'
          : 'bg-ink-100 text-ink-600';
  const label =
    status === 'succeeded'
      ? t('crm_import.status_succeeded')
      : status === 'failed'
        ? t('crm_import.status_failed')
        : status === 'running'
          ? t('crm_import.status_running')
          : t('crm_import.status_pending');
  return (
    <span className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 ${color}`}>
      {label}
    </span>
  );
}

function isCrmProvider(p: string): boolean {
  return p === 'salesforce' || p === 'hubspot' || p === 'pipedrive';
}

function ConnectorRow({
  install,
  workspaceId,
  planLocked,
}: {
  install: ConnectorInstall;
  workspaceId: string;
  planLocked: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [showRuns, setShowRuns] = useState(false);
  const crm = isCrmProvider(install.provider);
  const isInbox = install.provider === 'gmail' || install.provider === 'm365';
  // CRM + inbox are both gated at the Growth tier today; CSV-only providers
  // (linkedin_csv, sansan_csv) stay free.
  const rowLocked = planLocked && (crm || isInbox);

  // Pull recent runs only when the expander is open (or for the inline summary).
  const runsQ = useQuery({
    queryKey: ['crm-runs', install.id],
    queryFn: () => crmImportApi.listRuns(install.id, 5),
    enabled: crm && (showRuns || true), // always fetch to show the latest counters summary
    refetchInterval: showRuns ? 5000 : false,
  });
  const lastRun = runsQ.data?.[0] ?? null;
  const recentRuns = (runsQ.data ?? []).slice(0, 5);

  const crmRun = useMutation({
    mutationFn: () => crmImportApi.run(install.id),
    onSuccess: () => {
      toast.success(t('crm_import.import_queued'));
      qc.invalidateQueries({ queryKey: ['crm-runs', install.id] });
      qc.invalidateQueries({ queryKey: ['connectors-installs', workspaceId] });
    },
    onError: () => {
      toast.error(t('crm_import.import_failed'));
    },
  });

  const inboxSync = useMutation({
    mutationFn: () => inboxSyncApi.run(install.id),
    onSuccess: () => {
      toast.success(t('inbox.sync_succeeded'));
      qc.invalidateQueries({ queryKey: ['connectors-installs', workspaceId] });
    },
    onError: () => {
      toast.error(t('inbox.sync_failed'));
    },
  });

  const disconnect = useMutation({
    mutationFn: () => connectorsApi.disconnect(install.id),
    onSuccess: () => {
      toast.success(t('connectors.disconnected'));
      qc.invalidateQueries({ queryKey: ['connectors-installs', workspaceId] });
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askDisconnect = async () => {
    const ok = await confirm({
      title: t('connectors.disconnect_confirm_title'),
      description: t('connectors.disconnect_confirm_body', { provider: providerLabel }),
      confirmLabel: t('connectors.disconnect'),
      variant: 'danger',
    });
    if (ok) disconnect.mutate();
  };

  const providerLabel = useMemo(() => {
    const map: Record<string, string> = {
      salesforce: 'Salesforce',
      hubspot: 'HubSpot',
      pipedrive: 'Pipedrive',
      gmail: 'Gmail',
      m365: 'Microsoft 365',
      linkedin_csv: 'LinkedIn (CSV)',
      sansan_csv: 'Sansan',
    };
    return map[install.provider] ?? install.provider;
  }, [install.provider]);

  return (
    <li className="rounded-2xl bg-white border border-ink-200 p-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-ink-900">{providerLabel}</h3>
            <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 bg-ink-100 text-ink-600">
              {install.status}
            </span>
            {rowLocked && <LockBadge required="growth" />}
          </div>
          {rowLocked && (
            <p className="mt-1 text-[11px] text-ink-500">
              {t('plan.locked_connector_hint')}
            </p>
          )}
          <div className="mt-1 text-xs text-ink-500">
            {install.lastSyncedAt ? (
              <>
                {t('crm_import.last_synced')}{' '}
                <span className="num">{relativeTime(install.lastSyncedAt)}</span>
              </>
            ) : (
              t('crm_import.never_synced')
            )}
          </div>
          {crm && lastRun && (
            <div className="mt-2 text-[11px] text-ink-600 flex flex-wrap gap-x-3 gap-y-0.5 items-center">
              {statusBadge(lastRun.status, t)}
              <span>
                {t('crm_import.counts_accounts')}:{' '}
                <span className="num">{lastRun.accountsCreated}</span>{' '}
                {t('crm_import.counts_created')},{' '}
                <span className="num">{lastRun.accountsUpdated}</span>{' '}
                {t('crm_import.counts_updated')}
              </span>
              <span>
                {t('crm_import.counts_contacts')}:{' '}
                <span className="num">{lastRun.contactsCreated}</span>{' '}
                {t('crm_import.counts_created')},{' '}
                <span className="num">{lastRun.contactsUpdated}</span>{' '}
                {t('crm_import.counts_updated')}
              </span>
            </div>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {crm && (
            <button
              type="button"
              onClick={() => crmRun.mutate()}
              disabled={crmRun.isPending || rowLocked}
              aria-disabled={rowLocked || undefined}
              title={rowLocked ? t('plan.locked_connector_hint') : undefined}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {crmRun.isPending ? t('crm_import.importing') : t('crm_import.import_now')}
            </button>
          )}
          {isInbox && (
            <button
              type="button"
              onClick={() => inboxSync.mutate()}
              disabled={inboxSync.isPending || rowLocked}
              aria-disabled={rowLocked || undefined}
              title={rowLocked ? t('plan.locked_connector_hint') : undefined}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {inboxSync.isPending ? t('crm_import.syncing') : t('crm_import.sync_now')}
            </button>
          )}
          {crm && (
            <button
              type="button"
              onClick={() => setShowRuns((v) => !v)}
              className="text-xs px-3 py-1.5 rounded-md text-ink-600 hover:bg-ink-100"
            >
              {showRuns ? t('crm_import.hide_runs') : t('crm_import.show_runs')}
            </button>
          )}
          <button
            type="button"
            onClick={askDisconnect}
            disabled={disconnect.isPending}
            className="text-xs px-3 py-1.5 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-60"
          >
            {t('connectors.disconnect')}
          </button>
        </div>
      </div>

      {crm && showRuns && (
        <div className="mt-4 border-t border-ink-200 pt-3">
          <h4 className="text-xs font-semibold text-ink-700 mb-2">
            {t('crm_import.runs_title')}
          </h4>
          {runsQ.isLoading && (
            <p className="text-xs text-ink-500">{t('common.loading')}</p>
          )}
          {!runsQ.isLoading && recentRuns.length === 0 && (
            <p className="text-xs text-ink-500">{t('crm_import.no_runs')}</p>
          )}
          {recentRuns.length > 0 && (
            <ul className="space-y-2">
              {recentRuns.map((run) => (
                <li
                  key={run.id}
                  className="rounded-md border border-ink-200 px-3 py-2 text-xs text-ink-700"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {statusBadge(run.status, t)}
                    <span className="num text-ink-500">{relativeTime(run.startedAt)}</span>
                    <span className="text-ink-500">·</span>
                    <span>
                      {t('crm_import.counts_accounts')}:{' '}
                      <span className="num">{run.accountsCreated}</span>{' '}
                      {t('crm_import.counts_created')},{' '}
                      <span className="num">{run.accountsUpdated}</span>{' '}
                      {t('crm_import.counts_updated')}
                    </span>
                    <span>
                      {t('crm_import.counts_contacts')}:{' '}
                      <span className="num">{run.contactsCreated}</span>{' '}
                      {t('crm_import.counts_created')},{' '}
                      <span className="num">{run.contactsUpdated}</span>{' '}
                      {t('crm_import.counts_updated')}
                    </span>
                  </div>
                  {run.error && (
                    <div className="mt-1 text-warn-700 text-[11px] break-words">
                      <span className="font-medium">{t('crm_import.error_label')}:</span>{' '}
                      {run.error}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
